
interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const anticelluliteMassage: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "anti-cellulite-massage",
        "meta": {
            "title": "Anti-Cellulite Massage in Dubai | Elite Body Home",
            "description": "Professional anti-cellulite massage to smooth and tone your skin, boost circulation, and break down fat deposits for a firmer, more contoured look.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/anti-cellulite-massage",
            "og": {
                "title": "Anti-Cellulite Massage in Dubai | Elite Body Home",
                "description": "Professional anti-cellulite massage to smooth and tone your skin in Dubai",
                "image": "https://www.elitebodyhome.com/images/anti-cellulite-massage-og.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Anti-Cellulite Massage"],
        "hero": {
            "title": "Anti-Cellulite Massage in Dubai",
            "subtitle": "Smooth and Tone Your Skin",
            "description": "Are you ready for the cellulite massage? This focused treatment helps smooth and tone your skin while giving your circulation a boost and breaking down fat deposits to bring you a firmer, more contoured look. Our expert therapists will use sophisticated techniques that yield visible results in a relaxing and rejuvenating experience. Get into such great shape that you feel sexy in your own skin with our Body Anti-Cellulite Massage at Elite Body!"
        },
        "howItWorks": {
            "title": "What is Anti-Cellulite Massage?",
            "description": "Anti-cellulite massage is a unique type of massage, aimed at eliminating fat deposits just under the skin, leading to an appearance that we call cellulite. This includes techniques like deep tissue manipulation, pressure methods, and the application of certain oils or creams to enhance blood flow and dissolve fat cells."
        },
        "procedureSteps": {
            "heading": "Procedure Steps For Anti-Cellulite Massage",
            "para": "Anti-cellulite massage combines specialized techniques to target cellulite and improve skin appearance through deep tissue work and improved circulation.",
            "steps": [
                {
                    "title": "Targeted Pressure",
                    "desc": "The massage therapist uses deep strokes and firm pressure to break down fat cells and increase blood flow.",
                    "icon": "fa-solid fa-hand-pressure"
                },
                {
                    "title": "Lymphatic Drainage",
                    "desc": "Special techniques encourage the release of toxic buildup and excess fluid to help fight bloating and swelling.",
                    "icon": "fa-solid fa-water"
                },
                {
                    "title": "Anti-Cellulite Creams/Oils",
                    "desc": "These products help amplify the massage, improving skin elasticity and helping burn fat.",
                    "icon": "fa-solid fa-bottle-droplet"
                },
                {
                    "title": "Skin Rolling & Kneading",
                    "desc": "This method works by separating fibrous bands beneath the skin that make up the texture of cellulite.",
                    "icon": "fa-solid fa-hands"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "Anti-cellulite massage has numerous benefits that complement the appearance of smooth, glowing skin",
            "para": "Regular treatments can lead to drastic improvement in skin health, body confidence and wellness.",
            "items": [
                {
                    "title": "Improved Skin Texture",
                    "desc": "The massage increases blood circulation and collagen production, resulting in firmer, smoother skin.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Cellulite Reduction",
                    "desc": "The massage breaks down fat cells and fibrous tissue, greatly diminishing the visible signs of cellulite.",
                    "icon": "fa-solid fa-minus"
                },
                {
                    "title": "Increased Circulation",
                    "desc": "Enhanced blood flow aids in removing toxins and improving skin tone and elasticity.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Lymphatic Drainage",
                    "desc": "Helps the natural drainage of the lymphatic system, reduces water retention and bloating.",
                    "icon": "fa-solid fa-leaf"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "Who Can Benefit?",
            "para": "Almost everyone can benefit from anti-cellulite massage, but some factors will dramatically influence the success of the treatment.",
            "items": [
                {
                    "title": "Mild to Moderate Cellulite",
                    "desc": "Best for patients with mild to moderate cellulite looking for a noninvasive solution.",
                    "icon": "fa-solid fa-check"
                },
                {
                    "title": "All Skin Types",
                    "desc": "All skin types, even sensitive skin, can tolerate anti-cellulite massage.",
                    "icon": "fa-solid fa-check"
                },
                {
                    "title": "Body Confidence Seekers",
                    "desc": "Those seeking to enhance the appearance of their skin and minimize cellulite for a confidence boost.",
                    "icon": "fa-solid fa-check"
                },
                {
                    "title": "Postpartum Women",
                    "desc": "After pregnancy, many women choose anti-cellulite massage to restore skin firmness and elasticity.",
                    "icon": "fa-solid fa-check"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "How to Prepare",
            "para": "A few preparations are necessary to ensure you have the most effective anti-cellulite massage.",
            "items": [
                {
                    "title": "Hydration",
                    "desc": "Hydrate before your session to help flush out toxins and improve circulation.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Avoid Heavy Meals",
                    "desc": "Plan on light meals right before your massage so you don't feel bloated during the session.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Wear Comfortable Clothing",
                    "desc": "Choose loose, comfy clothes that can be slipped off for the treatment.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "Avoid Caffeine",
                    "desc": "Avoid excessive caffeine consumption prior to the massage to decrease water retention.",
                    "icon": "fa-solid fa-mug-hot"
                },
                {
                    "title": "Discuss Health Conditions",
                    "desc": "Tell your therapist about any medical conditions, allergies or areas of discomfort to tailor the massage accordingly.",
                    "icon": "fa-solid fa-clipboard-question"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "What to Expect",
            "para": "Anti-cellulite massage can show amazing results with regular sessions and proper maintenance. When combined with a healthy lifestyle, the treatment not only smooths skin, but can offer long-term benefits.",
            "items": [
                {
                    "title": "Smoother, Firmer Skin",
                    "desc": "After a few sessions, skin will look visibly smoother and firmer with less dimpling and uneven texture.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "Improved Blood Circulation",
                    "desc": "This increased blood flow carries more nutrients to the skin, allowing it to appear healthier and more vibrant.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Boost in Confidence",
                    "desc": "Many women claim that knowing their cellulite has drastically decreased has made them feel more confident and comfortable in their own skin.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Long-Lasting Effects",
                    "desc": "With proper aftercare including hydration, exercise and a balanced diet, the results can last for months.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Tips",
            "subheading": "Maintaining Your Results",
            "para": "To maximize and maintain the results of your anti-cellulite massage, follow these aftercare recommendations:",
            "items": [
                {
                    "title": "Stay Hydrated",
                    "desc": "Drink plenty of water to help flush out toxins released during the massage.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Regular Exercise",
                    "desc": "Combine your massage sessions with regular physical activity to enhance fat burning and circulation.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "Healthy Diet",
                    "desc": "Maintain a balanced diet low in processed foods and sugars to prevent new fat accumulation.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "Regular Sessions",
                    "desc": "Schedule maintenance sessions as recommended by your therapist to sustain results.",
                    "icon": "fa-solid fa-calendar-days"
                },
                {
                    "title": "Dry Brushing",
                    "desc": "Consider dry brushing between sessions to stimulate circulation and lymphatic drainage.",
                    "icon": "fa-solid fa-broom"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "Common questions about anti-cellulite massage",
            "items": [
                {
                    "question": "How many sessions will I need?",
                    "answer": "Most clients see noticeable results after 6-8 sessions, with maintenance sessions recommended monthly thereafter. The exact number depends on your cellulite severity and goals."
                },
                {
                    "question": "Is the massage painful?",
                    "answer": "While firm pressure is used, the massage should not be painful. Some areas may feel tender, but therapists adjust pressure to your comfort level."
                },
                {
                    "question": "How soon will I see results?",
                    "answer": "You may notice improved skin texture after the first session, but significant cellulite reduction typically becomes visible after 4-6 sessions."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Some temporary redness or mild bruising may occur, but these typically fade within a day or two."
                },
                {
                    "question": "Can I combine this with other treatments?",
                    "answer": "Yes, anti-cellulite massage complements other body treatments like radiofrequency or cavitation for enhanced results."
                },
                {
                    "question": "How long does each session last?",
                    "answer": "Sessions typically last 60-90 minutes, depending on the areas being treated."
                },
                {
                    "question": "Is there any downtime?",
                    "answer": "No, you can resume normal activities immediately after your session."
                }
            ]
        },
        "treatmentAreas1": [
            {
                "ar": "الفخذين",
                "title": "Thighs",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/thighs-cellulite.jpg"
            },
            {
                "ar": "الأرداف",
                "title": "Buttocks",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/buttocks-cellulite.jpg"
            },
            {
                "ar": "البطن",
                "title": "Abdomen",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/abdomen-cellulite.jpg"
            }
        ],
        "treatmentAreas2": [
            {
                "ar": "الذراعين",
                "title": "Arms",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/arms-cellulite.jpg"
            },
            {
                "ar": "الخصر",
                "title": "Waist",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/waist-cellulite.jpg"
            },
            {
                "ar": "الظهر",
                "title": "Back",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/back-cellulite.jpg"
            }
        ]
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Bandgfdxner-Image-8-scaled.jpg",
            "beforeAfter": [
                {
                    "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Anti-Cellulite-2.webp",
                    "alt": "Anti-Cellulite Treatment Result 2"
                },
                {
                    "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Anti-Cellulite-1.webp",
                    "alt": "Anti-Cellulite Treatment Result 1"
                },
                {
                    "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Anti-Cellulite-3.webp",
                    "alt": "Anti-Cellulite Treatment Result 3"
                }
            ]

        },
        "product": {
            "id": 162,
            "title": "Anti-Cellulite Massage in Dubai",
            "description": "Professional anti-cellulite massage to smooth and tone your skin, boost circulation, and break down fat deposits for a firmer, more contoured look.",
            "price": 299
        }
    },
    "ar": {
        "url": "anti-cellulite-massage",
        "meta": {
            "title": "علاج السيلوليت بالمساج في دبي | ايليت بودي هوم",
            "description": "جلسات مساج متخصصة لعلاج السيلوليت، تنعيم البشرة، تنشيط الدورة الدموية، وتفتيت التكتلات الدهنية للحصول على مظهر أكثر تناسقًا وتماسكًا.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/anti-cellulite-massage",
            "og": {
                "title": "علاج السيلوليت بالمساج في دبي | ايليت بودي هوم",
                "description": "جلسات مساج متخصصة لعلاج السيلوليت في دبي",
                "image": "https://www.elitebodyhome.com/images/anti-cellulite-massage-og.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج السيلوليت بالمساج"],
        "hero": {
            "title": "علاج السيلوليت بالمساج في دبي",
            "subtitle": "تنعيم وشد البشرة",
            "description": "وأخيرًا، هل أنتِ مستعدة لتجربة مساج السيلوليت؟ يعمل هذا العلاج المركز على تنعيم وشد البشرة، مع تنشيط الدورة الدموية وتفتيت التكتلات الدهنية، ليمنحكِ مظهرًا أكثر تماسكًا وتناسقًا. سيقوم معالجونا الخبراء باستخدام تقنيات متقدمة تمنحك نتائج مرئية، ضمن تجربة مريحة ومنعشة. استعيدي رشاقتك وتألقك مع جلسة المساج لعلاج السيلوليت في راحة منزلك مع ايليت بودي هوم– لتشعري بالثقة وجمال بشرتك!"
        },
        "howItWorks": {
            "title": "ما هو مساج السيلوليت؟",
            "description": "يُعد مساج التخلص من السيلوليت نوع مميز من التدليك، يهدف إلى تفتيت التراكمات الدهنية تحت سطح الجلد، والتي تُسبب المظهر المعروف باسم السيلوليت. تتضمن هذه التقنية أساليب متعددة مثل تدليك الأنسجة العميقة، والعلاج بالضغط، واستخدام زيوت وكريمات خاصة لتحفيز الدورة الدموية والمساعدة في تفكيك الخلايا الدهنية."
        },
        "procedureSteps": {
            "heading": "خطوات علاج السيلوليت بالمساج",
            "para": "يجمع مساج السيلوليت بين تقنيات متخصصة تستهدف السيلوليت وتحسن مظهر البشرة من خلال العمل على الأنسجة العميقة وتحسين الدورة الدموية.",
            "steps": [
                {
                    "title": "العلاج بالضغط",
                    "desc": "يستخدم المعالج ضغطًا قويًا للمساعدة في تفكيك الخلايا الدهنية وتحفيز تدفق الدم في المنطقة المستهدفة.",
                    "icon": "fa-solid fa-hand-pressure"
                },
                {
                    "title": "تحسين تصريف السوائل اللمفاوية",
                    "desc": "يُستخدم أسلوب خاص لتحفيز الجسم على تعزيز التصريف اللمفاوي والتخلص من السموم ويخفف من احتباس السوائل، مما يساعد على تقليل الانتفاخ والتورم.",
                    "icon": "fa-solid fa-water"
                },
                {
                    "title": "كريمات وزيوت لعلاج السيلوليت",
                    "desc": "تساعد هذه المواد في تعزيز فاعلية جلسات التدليك، مما يُحسِّن من مرونة البشرة ويساعد أيضًا على التخلص من الدهون.",
                    "icon": "fa-solid fa-bottle-droplet"
                },
                {
                    "title": "استخدام البكر وتدليك الجلد",
                    "desc": "تعمل هذه الطريقة على فصل الأنسجة الليفية تحت الجلد للمُسببة لمظهر السيلوليت.",
                    "icon": "fa-solid fa-hands"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "يُعتبر مساج السيلوليت من أكثر الطرق فاعلية لتحسين مظهر البشرة",
            "para": "حيث تظهر نتائج ملحوظة على صحة الجلد، والثقة بالنفس، والصحة العامة عند الانتظام على العلاج.",
            "items": [
                {
                    "title": "تحسين ملمس البشرة",
                    "desc": "يساهم هذا النوع من التدليك في تحسين الدورة الدموية وتحفيز إنتاج الكولاجين، مما يؤدي إلى بشرة أكثر تماسكًا ونعومة.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "التقليل من مظهر السيلوليت",
                    "desc": "يساعد المساج على تفكيك الخلايا الدهنية والنسيج الليفي تحت الجلد، مما يقلل بشكل كبير من مظهر السيلوليت.",
                    "icon": "fa-solid fa-minus"
                },
                {
                    "title": "تنشيط الدورة الدموية",
                    "desc": "يعزز تدفق الدم في المناطق المعالجة، مما يساعد على التخلص من السموم وتحسين لون البشرة ومرونتها.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "تعزيز التصريف الليمفاوي",
                    "desc": "يدعم المساج عملية التصريف الطبيعية للسائل الليمفاوي، مما يُقلل من احتباس السوائل والانتفاخ.",
                    "icon": "fa-solid fa-leaf"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "من يمكنه الاستفادة؟",
            "para": "يمكن لمعظم الأشخاص الاستفادة من جلسات علاج السيلوليت بالمساج، ولكن هناك بعض العوامل التي تؤثر بشكل كبير على فاعلية العلاج.",
            "items": [
                {
                    "title": "السيلوليت الخفيف إلى المتوسط",
                    "desc": "تُعد الجلسات مثالية للأشخاص الذين يعانون من السيلوليت بدرجة خفيفة إلى متوسطة، ويبحثون عن حل غير جراحي لتحسين مظهر الجلد.",
                    "icon": "fa-solid fa-check"
                },
                {
                    "title": "جميع أنواع البشرة",
                    "desc": "يمكن أن تتحمل جميع أنواع البشرة جلسات المساج لعلاج السيلوليت، بما في ذلك البشرة الحساسة.",
                    "icon": "fa-solid fa-check"
                },
                {
                    "title": "الراغبون في تعزيز الثقة بالنفس",
                    "desc": "يستفيد من هذه الجلسات كل من يسعى لتحسين مظهر بشرته وتقليل السيلوليت من أجل تعزيز ثقته بجسمه ومظهره العام.",
                    "icon": "fa-solid fa-check"
                },
                {
                    "title": "النساء بعد الولادة",
                    "desc": "تُعتبر جلسات إزالة السيلوليت خيارًا مفضلًا للعديد من النساء بعد الحمل، لاستعادة مرونة الجلد وتماسكه بطريقة آمنة وطبيعية.",
                    "icon": "fa-solid fa-check"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "كيف تستعد للجلسة",
            "para": "يجب الالتزام ببعض التحضيرات البسيطة التي تساهم في تحسين فاعلية العلاج وتعزيز فوائده، لتحقيق أفضل النتائج من جلسة علاج السيلوليت بالمساج.",
            "items": [
                {
                    "title": "الحفاظ على ترطيب الجسم",
                    "desc": "اشرب كمية كافية من الماء قبل الجلسة، ليساعد على طرد السموم من الجسم وتنشيط الدورة الدموية.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "تجنب الوجبات الثقيلة",
                    "desc": "احرص على تناول وجبة خفيفة قبل الجلسة لتجنب الشعور بالانتفاخ أثناء العلاج.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "ارتداء ملابس مريحة",
                    "desc": "اختر ملابس فضفاضة وسهلة الخلع، لتتمكن من الاسترخاء التام أثناء الجلسة.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "الامتناع عن الكافيين",
                    "desc": "امتنع عن الاستهلاك المفرط الكافيين قبل المساج، حيث يمكن أن يسبب احتباس السوائل في الجسم.",
                    "icon": "fa-solid fa-mug-hot"
                },
                {
                    "title": "مناقشة الحالات الصحية",
                    "desc": "أخبر المعالج بأي حالات صحية أو حساسية أو أي مناطق في الجسم تعاني من الألم، ليتمكن من تخصيص المساج بما يتناسب مع احتياجاتك وضمان سلامتك.",
                    "icon": "fa-solid fa-clipboard-question"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "ما يمكن توقعه",
            "para": "يمكن أن يحقق مساج التخلص من السيلوليت نتائج رائعة مع الانتظام على الجلسات واتباع نمط حياة صحي. لا يقتصر هذا النوع من العلاج على تنعيم الجلد فقط، بل يقدم فوائد طويلة الأمد على المظهر العام للبشرة وصحة الجسم.",
            "items": [
                {
                    "title": "بشرة أكثر نعومة وتماسكاً",
                    "desc": "ستلاحظ تحسنًا واضحًا في ملمس الجلد بعد عدة جلسات، حيث يصبح الجلد أكثر نعومة وتماسكًا، مع تقليل واضح لمظهر التعرجات والتموجات.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "تنشيط الدورة الدموية",
                    "desc": "يساعد تحسن تدفق الدم على إيصال العناصر الغذائية إلى الجلد، مما يمنحه مظهراً أكثر صحة وحيوية.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "تشعر الكثير من النساء بتحسن في مستوى ثقتهن بأنفسهن بعد تخفيف مظهر السيلوليت، والشعور براحة أكبر مع أجسادهن.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "نتائج تدوم طويلًا",
                    "desc": "يمكن أن تستمر النتائج لعدة أشهر عند الالتزام بالعناية اللاحقة مثل شرب الماء بوفرة، وممارسة الرياضة، واتباع نظام غذائي متوازن.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح العناية بعد الجلسة",
            "subheading": "للحفاظ على النتائج",
            "para": "لتعظيم النتائج والحفاظ عليها بعد جلسة مساج السيلوليت، ننصح باتباع هذه الإرشادات:",
            "items": [
                {
                    "title": "الحفاظ على ترطيب الجسم",
                    "desc": "اشرب كميات كافية من الماء للمساعدة في طرد السموم التي تم إطلاقها أثناء الجلسة.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "ممارسة الرياضة بانتظام",
                    "desc": "اجمع بين جلسات المساج والنشاط البدني المنتظم لتعزيز حرق الدهون وتحسين الدورة الدموية.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "نظام غذائي صحي",
                    "desc": "حافظ على نظام غذائي متوازن قليل الأطعمة المصنعة والسكريات لمنع تراكم الدهون الجديدة.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "جلسات منتظمة",
                    "desc": "حدد مواعيد جلسات الصيانة حسب توصية المعالج للحفاظ على النتائج.",
                    "icon": "fa-solid fa-calendar-days"
                },
                {
                    "title": "التدليك بالفرشاة الجافة",
                    "desc": "فكري في استخدام الفرشاة الجافة بين الجلسات لتحفيز الدورة الدموية والتصريف اللمفاوي.",
                    "icon": "fa-solid fa-broom"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "أسئلة شائعة حول مساج السيلوليت",
            "items": [
                {
                    "question": "كم عدد الجلسات التي سأحتاجها؟",
                    "answer": "معظم العملاء يلاحظون نتائج واضحة بعد 6-8 جلسات، مع جلسات صيانة شهرية بعد ذلك. يعتمد العدد الدقيق على شدة السيلوليت وأهدافك."
                },
                {
                    "question": "هل المساج مؤلم؟",
                    "answer": "بينما يتم استخدام ضغط قوي، لا ينبغي أن يكون المساج مؤلمًا. قد تشعرين ببعض الألم في بعض المناطق، لكن المعالجين يعدلون الضغط حسب مستوى راحتك."
                },
                {
                    "question": "متى سأرى النتائج؟",
                    "answer": "قد تلاحظين تحسنًا في ملمس البشرة بعد الجلسة الأولى، لكن التخفيف الملحوظ من السيلوليت يظهر عادة بعد 4-6 جلسات."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "قد يحدث احمرار مؤقت أو كدمات خفيفة، لكنها تختفي عادة خلال يوم أو يومين."
                },
                {
                    "question": "هل يمكنني الجمع بين هذا العلاج وعلاجات أخرى؟",
                    "answer": "نعم، يمكن دمج مساج السيلوليت مع علاجات الجسم الأخرى مثل الترددات الراديوية أو الكافيتيشن لتحسين النتائج."
                },
                {
                    "question": "كم تستغرق كل جلسة؟",
                    "answer": "تستغرق الجلسات عادة من 60 إلى 90 دقيقة، حسب المناطق التي يتم علاجها."
                },
                {
                    "question": "هل هناك فترة نقاهة؟",
                    "answer": "لا، يمكنك استئناف أنشطتك الطبيعية مباشرة بعد الجلسة."
                }
            ]
        },
        "treatmentAreas1": [
            {
                "ar": "الفخذين",
                "title": "الفخذين",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/thighs-cellulite.jpg"
            },
            {
                "ar": "الأرداف",
                "title": "الأرداف",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/buttocks-cellulite.jpg"
            },
            {
                "ar": "البطن",
                "title": "البطن",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/abdomen-cellulite.jpg"
            }
        ],
        "treatmentAreas2": [
            {
                "ar": "الذراعين",
                "title": "الذراعين",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/arms-cellulite.jpg"
            },
            {
                "ar": "الخصر",
                "title": "الخصر",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/waist-cellulite.jpg"
            },
            {
                "ar": "الظهر",
                "title": "الظهر",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/back-cellulite.jpg"
            }
        ]
    }
}